<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactStageChip from 'dashboard/components-next/Contacts/ContactDetail/ContactStageChip.vue';
import ContactEditDialog from 'dashboard/components-next/Contacts/ContactDetail/ContactEditDialog.vue';
import ContactInlineField from 'dashboard/components-next/Contacts/ContactDetail/ContactInlineField.vue';
import ContactAttributesCard from 'dashboard/components-next/Contacts/ContactDetail/ContactAttributesCard.vue';
import ConversationLabels from './labels/LabelBox.vue';
import ContactTasks from './contact/ContactTasks.vue';
import {
  findListAttribute,
  normalizeStage,
  STAGE_ATTRIBUTE_HINT,
} from 'dashboard/components-next/Contacts/contactStageColors';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import {
  shortDate,
  shortDateTime,
} from 'dashboard/components-next/Contacts/contactTime';
import {
  isTrackingAttribute,
  trackingLabel,
  DEFAULT_TRACKING_KEYS,
} from 'dashboard/components-next/Contacts/contactTracking';

// Top of the conversation rail (CrmB): CONTACTO · ETIQUETAS · EMBUDO ·
// TAREAS · SEGUIMIENTO UTM as small white cards. Everything below it (the
// draggable accordions) keeps working as before.
const props = defineProps({
  contact: { type: Object, required: true },
  conversationId: { type: [Number, String], required: true },
});

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();

const editDialogRef = ref(null);
const openEditDialog = () => editDialogRef.value?.open(props.contact);

const contactProfileLink = computed(
  () => `/app/accounts/${route.params.accountId}/contacts/${props.contact.id}`
);

const additionalAttributes = computed(
  () => props.contact.additional_attributes || {}
);
const customAttributes = computed(() => props.contact.custom_attributes || {});

// Los datos del contacto se editan aquí mismo; la ficha completa sigue en
// el panel de "Editar", que abre el formulario entero.
const fields = computed(() => [
  {
    key: 'phone_number',
    label: t('CONVERSATION_SIDEBAR.RAIL.PHONE'),
    value: props.contact.phone_number,
    type: 'tel',
  },
  // Llegan solos con cada mensaje de WhatsApp: se muestran si existen, no se editan.
  ...(additionalAttributes.value.whatsapp_username
    ? [
        {
          key: 'whatsapp_username',
          label: t('CONVERSATION_SIDEBAR.RAIL.WHATSAPP_USERNAME'),
          value: `@${additionalAttributes.value.whatsapp_username}`,
          readonly: true,
        },
      ]
    : []),
  ...(additionalAttributes.value.whatsapp_user_id
    ? [
        {
          key: 'whatsapp_user_id',
          label: t('CONVERSATION_SIDEBAR.RAIL.WHATSAPP_USER_ID'),
          value: additionalAttributes.value.whatsapp_user_id,
          readonly: true,
          mono: true,
        },
      ]
    : []),
  {
    key: 'email',
    label: t('CONVERSATION_SIDEBAR.RAIL.EMAIL'),
    value: props.contact.email,
    type: 'email',
  },
  {
    key: 'company_name',
    label: t('CONVERSATION_SIDEBAR.RAIL.COMPANY'),
    value:
      props.contact.company?.name || additionalAttributes.value.company_name,
  },
  {
    key: 'first_contact',
    label: t('CONVERSATION_SIDEBAR.RAIL.FIRST_CONTACT'),
    value: shortDate(props.contact.created_at, locale.value),
    readonly: true,
  },
]);

const savingField = ref(null);

// La empresa vive en additional_attributes; el resto son campos del contacto.
const saveField = async (key, value) => {
  savingField.value = key;
  const payload =
    key === 'company_name'
      ? {
          additionalAttributes: {
            ...additionalAttributes.value,
            companyName: value,
          },
        }
      : { [key === 'phone_number' ? 'phoneNumber' : key]: value };
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      ...payload,
    });
    useAlert(t('CONVERSATION_SIDEBAR.RAIL.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  } finally {
    savingField.value = null;
  }
};

// ---- Atributos sincronizados con etiquetas ----------------------------------
const contactAttributes = useMapGetter('attributes/getContactAttributes');

// Tipo, Estado y cualquier otro atributo marcado para sincronizar con
// etiquetas: una fila fija y editable por cada uno.
const chipAttributes = computed(() =>
  syncedListAttributes(contactAttributes.value)
);
// El embudo sigue anclado al estado del contacto, que es el atributo que
// registra las transiciones.
const stageAttribute = computed(() =>
  findListAttribute(
    contactAttributes.value,
    'estado_contacto',
    STAGE_ATTRIBUTE_HINT
  )
);
const attributeValue = attribute =>
  attribute ? customAttributes.value[attribute.attributeKey] || '' : '';

const updateAttribute = async (attribute, value) => {
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      customAttributes: { [attribute.attributeKey]: value },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

// ---- Embudo -------------------------------------------------------------------
// The stage history comes from contact_stage_transitions (recorded by the
// backend on every change). The card shows only the states the contact
// actually had, in date order: skipped stages never appear.
const transitions = ref([]);
const isLoadingTransitions = ref(false);

const loadTransitions = async () => {
  if (!props.contact?.id) return;
  isLoadingTransitions.value = true;
  try {
    const { data } = await ContactAPI.getStageTransitions(props.contact.id);
    transitions.value = data?.payload || [];
  } catch {
    transitions.value = [];
  } finally {
    isLoadingTransitions.value = false;
  }
};

watch(
  () => props.contact?.id,
  () => loadTransitions(),
  { immediate: true }
);
// Refetch after the stage changes from this rail or the header.
watch(
  () => attributeValue(stageAttribute.value),
  (next, prev) => {
    if (prev !== undefined && next !== prev) loadTransitions();
  }
);

const stageTransitions = computed(() => {
  const key = stageAttribute.value?.attributeKey;
  if (!key) return [];
  return transitions.value
    .filter(transition => transition.attribute_key === key)
    .sort((a, b) => a.transitioned_at - b.transitioned_at);
});

const funnel = computed(() => {
  const current = attributeValue(stageAttribute.value);

  // Solo el histórico real: cada estado que el contacto tuvo, en orden de
  // fecha. Si saltó etapas (de No contactado directo a Spam) se muestran
  // únicamente las que se le asignaron, no el orden configurado.
  const path = [];
  const pushStep = (value, at) => {
    if (!value) return;
    const existing = path.find(step => step.value === value);
    // Si vuelve a un estado anterior, se queda la fecha más reciente.
    if (existing) {
      if (at) existing.at = at;
      return;
    }
    path.push({ value, at });
  };

  const [first] = stageTransitions.value;
  if (first?.old_value) pushStep(first.old_value, props.contact.created_at);
  stageTransitions.value.forEach(transition =>
    pushStep(transition.new_value, transition.transitioned_at)
  );
  // Sin histórico (contactos anteriores a que se registrara) queda el
  // estado actual como único paso.
  if (current) pushStep(current, path.length ? null : props.contact.created_at);

  return path.map(step => {
    const isLost = /perdid|spam/.test(normalizeStage(step.value));
    return {
      value: step.value,
      when: step.at ? shortDate(step.at, locale.value) : '',
      reached: !isLost,
      lost: isLost,
      current: step.value === current,
    };
  });
});

// ---- Seguimiento · UTM --------------------------------------------------------
// Always visible (even without data) and editable in place: click a value,
// type, Enter saves, Esc cancels. Rows come from the account's tracking
// attributes; without any defined, the six standard UTM keys are offered.
const trackingRows = computed(() => {
  const defined = (contactAttributes.value || [])
    .filter(isTrackingAttribute)
    .map(attribute => ({
      key: attribute.attributeKey,
      label: trackingLabel(
        attribute.attributeKey,
        attribute.attributeDisplayName
      ),
    }));
  const rows = defined.length
    ? defined
    : DEFAULT_TRACKING_KEYS.map(key => ({ key, label: trackingLabel(key) }));
  return rows.map(row => ({
    ...row,
    value: customAttributes.value[row.key] ?? '',
  }));
});

const savingTrackingKey = ref(null);

const saveTracking = async (key, value) => {
  savingTrackingKey.value = key;
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      customAttributes: { [key]: value || null },
    });
    useAlert(t('CONVERSATION_SIDEBAR.RAIL.TRACKING_SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  } finally {
    savingTrackingKey.value = null;
  }
};

const firstVisit = computed(() =>
  props.contact.created_at
    ? t('CONVERSATION_SIDEBAR.RAIL.FIRST_VISIT', {
        date: shortDateTime(props.contact.created_at, locale.value),
      })
    : ''
);

const CARD_CLASS =
  'flex flex-col gap-2 px-3.5 py-3 rounded-[10px] border border-n-weak bg-n-solid-2';
const CARD_TITLE_CLASS =
  'text-[10.5px] font-semibold tracking-[0.1em] uppercase text-n-slate-10';
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- CONTACTO -->
    <section :class="CARD_CLASS">
      <div class="flex items-center gap-2">
        <span :class="CARD_TITLE_CLASS">
          {{ t('CONVERSATION_SIDEBAR.RAIL.CONTACT') }}
        </span>
        <a
          :href="contactProfileLink"
          target="_blank"
          rel="noopener noreferrer"
          class="ml-auto text-n-slate-9 hover:text-n-blue-text"
          :title="t('CONVERSATION_SIDEBAR.RAIL.OPEN_PROFILE')"
        >
          <Icon icon="i-lucide-external-link" size="size-3.5" />
        </a>
        <button
          type="button"
          class="inline-flex items-center gap-1 h-[22px] px-2 rounded-md bg-n-alpha-2 text-[11.5px] font-semibold text-n-blue-text hover:bg-n-solid-blue transition-colors"
          @click="openEditDialog"
        >
          <Icon icon="i-lucide-pencil" size="size-[11px]" />
          {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT') }}
        </button>
      </div>
      <div class="flex flex-col gap-[7px]">
        <ContactInlineField
          v-for="field in fields"
          :key="field.key"
          :label="field.label"
          :value="field.value"
          :type="field.type || 'text'"
          :readonly="field.readonly"
          :mono="field.mono"
          :is-saving="savingField === field.key"
          @save="value => saveField(field.key, value)"
        />
        <div
          v-for="(attribute, index) in chipAttributes"
          :key="attribute.id"
          class="flex items-center gap-2.5"
        >
          <span
            class="flex-shrink-0 w-[78px] text-[11.5px] text-n-slate-10 truncate"
            :title="attribute.attributeDisplayName"
          >
            {{ attribute.attributeDisplayName }}
          </span>
          <ContactStageChip
            :attribute="attribute"
            :value="attributeValue(attribute)"
            :show-dot="index === 0"
            @update="updateAttribute(attribute, $event)"
          />
        </div>
        <!-- Los atributos personalizados son del contacto: van aquí, no en
             una sección aparte. -->
        <ContactAttributesCard :contact="contact" variant="plain" />
      </div>
      <ContactEditDialog ref="editDialogRef" />
    </section>

    <!-- ETIQUETAS (only the ones not covered by Tipo / Estado) -->
    <section :class="CARD_CLASS">
      <span :class="CARD_TITLE_CLASS">
        {{ t('CONVERSATION_SIDEBAR.RAIL.LABELS') }}
      </span>
      <ConversationLabels :conversation-id="conversationId" only-other-labels />
    </section>

    <!-- EMBUDO -->
    <section v-if="funnel.length" :class="CARD_CLASS">
      <span :class="CARD_TITLE_CLASS">
        {{ t('CONVERSATION_SIDEBAR.RAIL.FUNNEL') }}
      </span>
      <div class="flex flex-col gap-2">
        <div
          v-for="step in funnel"
          :key="step.value"
          class="flex items-center gap-[9px] min-w-0"
        >
          <span
            class="size-[9px] rounded-full flex-shrink-0"
            :class="{
              'bg-n-brand': step.reached,
              'bg-n-ruby-9': step.lost,
              'bg-n-solid-2 shadow-[inset_0_0_0_1.5px_#c3ced7]':
                !step.reached && !step.lost,
            }"
          />
          <span
            class="text-[12.5px] truncate"
            :class="
              step.reached || step.lost
                ? 'font-medium text-n-slate-12'
                : 'text-n-slate-9'
            "
          >
            {{ step.value }}
          </span>
          <span class="ml-auto text-[11px] whitespace-nowrap text-n-slate-9">
            {{ step.when || '—' }}
          </span>
        </div>
      </div>
    </section>

    <!-- TAREAS -->
    <section class="!px-0 !pb-1" :class="[CARD_CLASS]">
      <span :class="CARD_TITLE_CLASS" class="px-3.5">
        {{ t('CONVERSATION_SIDEBAR.RAIL.TASKS') }}
      </span>
      <ContactTasks
        :contact-id="contact.id"
        :conversation-id="conversationId"
        class="-mt-1"
      />
    </section>

    <!-- SEGUIMIENTO · UTM -->
    <section :class="CARD_CLASS">
      <div class="flex items-center justify-between gap-2 min-w-0">
        <span
          :class="CARD_TITLE_CLASS"
          class="whitespace-nowrap !tracking-[0.06em]"
        >
          {{ t('CONVERSATION_SIDEBAR.RAIL.TRACKING') }}
        </span>
        <span
          v-if="firstVisit"
          class="text-[10px] font-semibold whitespace-nowrap text-n-slate-9"
        >
          {{ firstVisit }}
        </span>
      </div>
      <div class="flex flex-col gap-[5px]">
        <ContactInlineField
          v-for="row in trackingRows"
          :key="row.key"
          mono
          :label="row.label"
          :value="row.value"
          :placeholder="
            t('CONVERSATION_SIDEBAR.RAIL.TRACKING_EDIT_PLACEHOLDER')
          "
          :is-saving="savingTrackingKey === row.key"
          @save="value => saveTracking(row.key, value)"
        />
      </div>
    </section>
  </div>
</template>
